import { DecimalPipe, formatNumber } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  LOCALE_ID,
  output,
} from '@angular/core';
import { SlotCompletionRewardComponent } from '@components/slot-completion-reward/slot-completion-reward.component';
import { SlotRequirementComponent } from '@components/slot-requirement/slot-requirement.component';
import { SFXDirective } from '@directives/sfx.directive';
import { commissionRarity } from '@helpers/commission/commission-requirement.ui';
import { commissionDisplayName } from '@helpers/commission/commission-turn-in';
import { getEntry } from '@helpers/content';
import { recipesProducing } from '@helpers/crafting/recipes';
import { kingdomSubviewShow } from '@helpers/engine';
import { playSFX } from '@helpers/engine/sfx';
import { formatDuration } from '@helpers/engine/timer';
import { bestiaryDropQuantityLabel } from '@helpers/kingdom/bestiary.ui';
import type {
  CommissionRequirementEntry,
  CommissionSlotDisplay,
  DroppedReward,
  EquipmentId,
  ItemId,
  KingdomSubview,
  TradeskillContent,
  TradeskillId,
} from '@interfaces';

@Component({
  selector: 'app-slot-commission',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SlotRequirementComponent,
    DecimalPipe,
    SlotCompletionRewardComponent,
    SFXDirective,
  ],
  templateUrl: './slot-commission.component.html',
})
export class SlotCommissionComponent {
  private locale = inject(LOCALE_ID);

  public row = input.required<CommissionSlotDisplay>();

  // True when already rendered inside the row's own location modal (a caravan's trade popup, a town's Quests tab) - hides the redundant travel button.
  public isInLocationModal = input(false);

  // Which system to call (caravan vs. town) - supplied by the caller so this component stays generic.
  public fulfill = input.required<() => Promise<boolean>>();

  public turnIn = output<void>();
  public travel = output<void>();

  public commissionName = computed(() =>
    commissionDisplayName(this.row().commission?.name ?? 'Commission'),
  );

  public rarityLabel = computed(() =>
    commissionRarity(this.row().requirementEntries),
  );

  public requirementTooltip(entry: CommissionRequirementEntry): string {
    const name = entry.content?.name ?? 'Unknown';
    const owned = formatNumber(entry.owned, this.locale);
    const quantity = formatNumber(entry.quantity, this.locale);
    return `${name} (${owned}/${quantity})`;
  }

  public etaLabel(seconds: number): string {
    return formatDuration(seconds);
  }

  // Commission rewards aren't level-scaled, so the level passed here is inert.
  public rewardQuantityLabel(reward: DroppedReward): string {
    return bestiaryDropQuantityLabel(reward, 1);
  }

  public async doTurnIn(): Promise<void> {
    if (!(await this.fulfill()())) return;
    playSFX('item-get-minor', 0);
    this.turnIn.emit();
  }

  public tradeskillForRequirement(
    requirement: CommissionRequirementEntry,
  ): TradeskillId | undefined {
    const isCraftable =
      requirement.kind === 'item' || requirement.kind === 'equipment';
    if (!isCraftable || !requirement.content) return undefined;

    const resultId = requirement.content.id as ItemId | EquipmentId;
    return recipesProducing(resultId)[0]?.tradeskillId;
  }

  public navigateToTradeskill(requirement: CommissionRequirementEntry): void {
    const tradeskillForRequirement = this.tradeskillForRequirement(requirement);
    if (!tradeskillForRequirement) return;

    const tradeskillRef = getEntry<TradeskillContent>(tradeskillForRequirement);
    if (!tradeskillRef) return;

    kingdomSubviewShow(
      `tradeskill-${tradeskillRef.name.toLowerCase()}` as KingdomSubview,
    );
  }
}
